<script lang="ts">
import { InterestType } from '@/app/subscription-ui/entities/interest.type';
import { SubscriptionState } from '@/app/subscription-ui/entities/subscription-state.enum';
import { Subscription } from '@/app/subscription-ui/entities/subscription.interface';
import { SubscriptionController } from '@/app/subscription-ui/subscription.controller';
import IconComponent from '@/components/atoms/Icon.vue';
import { TranslationKey, TranslationService } from 'webshop-modules';
import { IconName } from 'webshop-modules';
import {Component, Vue, Inject} from "vue-facing-decorator"

@Component({
    name: 'SubscriptionUi',
    components: {
        IconComponent,
    }
})
export default class SubscriptionUi extends Vue {
    @Inject() 
    public controller!: SubscriptionController;
    @Inject() 
    public translationService!: TranslationService;

    public readonly IconName: typeof IconName = IconName;
    public readonly InterestType: typeof InterestType = InterestType;
    public readonly SubscriptionState: typeof SubscriptionState = SubscriptionState;
    public readonly Translation: typeof TranslationKey = TranslationKey;

    public emailInputLabel = '';

    get subscription(): Subscription {
        return this.controller.getState();
    }

    public submitSubscription(): void {
        const state = this.controller.getState();

        if (state.requestInProgress) {
            return;
        }

        if (state.user.email === null || state.user.email.length === 0) {
            return;
        }

        this.controller.createSubscription();
    }

    public async mounted(): Promise<void> {
        this.emailInputLabel = await this.translationService.getTranslationFor(TranslationKey.User_enter_your_email);
    }

}
</script>

<template>
    <div class="m-panel m-panel--newsletter">
        <div class="m-panel__head">
            <IconComponent :icon-id="IconName.Interest" />
        </div>

        <div class="m-panel__body" v-if="subscription.displayState !== SubscriptionState.SuccessfulRegistration">
            <div class="m-panel__row">
                <template v-if="subscription.interest.type === InterestType.General">
                    <h2 v-translate="{ key: Translation.Interest_subscription_general_interest_title }"></h2>
                    <p v-translate="{ key: Translation.Interest_subscription_general_interest_description }"></p>
                </template>

                <template v-if="subscription.interest.type === InterestType.Category || subscription.interest.type === InterestType.MainCategory">
                    <h2 v-translate="{
                        key: Translation.Interest_subscription_category_interest_title,
                        keyValues: JSON.stringify({ 'tdlCategory': subscription.interest.resourceName })
                    }"></h2>
                    <p v-translate="{
                        key: Translation.Interest_subscription_category_interest_description,
                        keyValues: JSON.stringify({ 'tdlCategory': subscription.interest.resourceName })
                    }"></p>
                </template>

                <template v-if="subscription.interest.type === InterestType.Artist">
                    <h2 v-translate="{
                        key: Translation.Interest_subscription_artist_interest_title,
                        keyValues: JSON.stringify({ 'artist': subscription.interest.resourceName })
                    }"></h2>
                    <p v-translate="{
                        key: Translation.Interest_subscription_artist_interest_description,
                        keyValues: JSON.stringify({ 'artist': subscription.interest.resourceName })
                    }"></p>
                </template>

                <template v-if="subscription.interest.type === InterestType.Venue">
                    <h2 v-translate="{
                        key: Translation.Interest_subscription_venue_interest_title,
                        keyValues: JSON.stringify({ 'venue': subscription.interest.resourceName })
                    }"></h2>
                    <p v-translate="{
                        key: Translation.Interest_subscription_venue_interest_description,
                        keyValues: JSON.stringify({ 'venue': subscription.interest.resourceName })
                    }"></p>
                </template>

                <div class="o-subscriptionForm">
                    <div class="a-formField">
                        <input
                            v-bind:value="subscription.user.email"
                            v-on:input="controller.updateStateEmail(($event.target as HTMLTextAreaElement).value)"
                            v-bind:placeholder="emailInputLabel"
                            v-bind:aria-label="emailInputLabel"
                            class="a-formInput" name="userEmail" type="email" />
                    </div>

                    <button
                        v-on:click.stop="submitSubscription()"
                        class="a-button -submit" type="submit"
                        v-bind:class="[{ 
                            '-active': !subscription.requestInProgress 
                                && subscription.user.email !== null 
                                && subscription.user.email.length !== 0 
                        }]">
                        <span v-translate="{ key: Translation.Notifications_subscribe }"></span>
                    </button>
                    <p v-html="subscription.gdprNotice"></p>
                    <p v-if="subscription.displayState === SubscriptionState.SubscriptionFailed"
                       v-translate="{ key: Translation.Interest_subscription_error }"
                       class="m-formGroup__notice"></p>
                </div>
            </div>
        </div>

        <div class="m-panel__body" v-if="subscription.displayState === SubscriptionState.SuccessfulRegistration">
            <div class="m-panel__row">
                <h2 v-if="subscription.interest.type === InterestType.General"
                    v-translate="{ key: Translation.Interest_subscription_general_success_title }"></h2>

                <h2 v-else
                    v-translate="{
                    key: Translation.Interest_subscription_success_title,
                    keyValues: JSON.stringify({ 'interestName': subscription.interest.resourceName })
                }"></h2>

                <p v-translate="{
                    key: Translation.Interest_subscription_success_description,
                    keyValues: JSON.stringify({ 'userEmail': subscription.user.email })
                }"></p>
            </div>
        </div>
    </div>
</template>
